import classNames from 'classnames';
import * as _ from 'lodash-es';
import type Quill from 'quill/core';
import { Delta } from 'quill/core';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';
import type { EditVersionChange } from './calculateVersionChanges';
import HistoricalPost from './HistoricalPost';
import type { PostHistoryMagneticWallVersionChanges } from './PostHistoryMagneticWall';
import * as styles from './VersionChanges.module.scss';
import type { ButtonProps } from '../../components/Button';
import Button from '../../components/Button';
import Caret from '../../components/Caret';
import OverflowMenu from '../../components/OverflowMenu';
import SVGIcon from '../../components/SVGIcon';
import AttributeHandler from '../../editor/AttributeHandler';
import type {
  MagneticWallFocus,
  MagneticWallType,
} from '../../editor/editorSlice';
import Annotation from '../../editor/formats/Annotation';
import Comment from '../../editor/formats/Comment';
import VersionChangeFormat from '../../editor/formats/VersionChange';
import { useQuill } from '../../editor/QuillContext';
import { useScrollingToAnchorOnLoad } from '../../editor/useAnchorScrollingHandler';
import useHeaderCollapse, {
  getHeaderCollapse,
} from '../../editor/useHeaderCollapse';
import useListCollapse, { getListCollapse } from '../../editor/useListCollapse';
import overflowMenuIcon from '../../icons/non-prefilled/16x16/overflowMenu.svg';
import retryIcon from '../../icons/non-prefilled/16x16/retry.svg';
import { useIsSize } from '../../utils/responsive/isSize';
import Sizes from '../../utils/responsive/Sizes';
import { getMagneticWallItemContentRects } from '../MagneticWall';
import type { RelativeContentRects } from '../MagneticWallLayout';
import { reduceContentRectsToMap } from '../MagneticWallLayout';
import PostLayout from '../PostLayout';
import usePostChromeVisible from '../usePostChromeVisible';
import getCollapsedElementRect from '../utils/getCollapsedElementRect';
import toRelativeDOMRect from '../utils/toRelativeDOMRect';

type VersionChange = EditVersionChange | PostHistoryMagneticWallVersionChanges;

type VersionChangesFocusContextValue = {
  focusedIndex: number;
  focusedVersionChange: VersionChange | null;
  focusById: (id: string | null) => void;
  goToNextChange: () => void;
  goToPreviousChange: () => void;
};

const VersionChangesFocusContext =
  React.createContext<VersionChangesFocusContextValue | null>(null);

export const useVersionChangesFocus = () => {
  const value = useContext(VersionChangesFocusContext);
  if (!value) {
    throw new Error(
      'useVersionChangesFocus must be used within a VersionChangesStateContext.Provider',
    );
  }
  return value;
};

type VersionChangesContextValue = {
  isInitialized: boolean;
  versionChanges: VersionChange[];
  updateVersionChanges: (quill: Quill) => void;
};

const VersionChangesContext =
  React.createContext<VersionChangesContextValue | null>(null);

export const useVersionChanges = () => {
  const value = useContext(VersionChangesContext);
  if (!value) {
    throw new Error(
      'useVersionChanges must be used within a VersionChangesContext.Provider',
    );
  }
  return value;
};

type VersionChangeMagneticWallFocusContextValue = {
  magneticWallChangeFocus: MagneticWallFocus | null;
  focusMagneticWallItem: (id: string, type: MagneticWallType) => void;
  blurMagneticWallFocus: () => void;
};

const VersionChangeMagneticWallFocusContext =
  React.createContext<VersionChangeMagneticWallFocusContextValue | null>(null);

export const useVersionChangeMagneticWallFocus = () => {
  const value = useContext(VersionChangeMagneticWallFocusContext);
  if (!value) {
    throw new Error(
      'useVersionChangeMagneticWallFocus must be used within a MagneticWallFocusContext.Provider',
    );
  }
  return value;
};

function isInlineElement(el: Element) {
  const d = getComputedStyle(el).display;
  return d === 'inline' || d.startsWith('inline-');
}

export function getVersionChangeContentRects(
  quill: Quill,
): RelativeContentRects[] {
  const containerRect = quill.container.getBoundingClientRect();
  const collapsedHeaders =
    getHeaderCollapse(quill)?.getCollapsedHeaders() ?? [];
  const collapsedListItems =
    getListCollapse(quill)?.getCollapsedListItems() ?? [];

  return Array.from(
    quill.container.querySelectorAll<HTMLElement>(
      `[${VersionChangeFormat.keyName}]`,
    ),
  ).flatMap((element) => {
    const id = VersionChangeFormat.value(element) as string;

    const collapsedElementRect = getCollapsedElementRect(element, [
      ...collapsedHeaders,
      ...collapsedListItems,
    ]);
    if (collapsedElementRect) {
      return [
        {
          rect: toRelativeDOMRect(collapsedElementRect, containerRect),
          id,
        },
      ];
    }

    const contentRect = element.getClientRects()[0];
    if (!contentRect) return [];

    const lineHeight = parseFloat(window.getComputedStyle(element).lineHeight);

    const finalContentRect = (() => {
      if (element instanceof HTMLParagraphElement) {
        return DOMRect.fromRect({
          x: contentRect.x + contentRect.width,
          y: contentRect.y + contentRect.height - lineHeight,
          width: 12,
          height: lineHeight,
        });
      }

      if (isInlineElement(element)) {
        // Line break diffs are 26px high, inline diffs are 24px high.
        // This means both could have different top values, but still be on the same line.
        // This can cause the ordering of the items in the magnetic wall layout to be incorrect.
        // In order to fix this, we normalize all content rects to top their line.
        return DOMRect.fromRect({
          ...contentRect,
          y: contentRect.y + contentRect.height / 2 - lineHeight / 2,
          height: lineHeight,
        });
      }

      return contentRect;
    })();

    return [
      {
        rect: toRelativeDOMRect(finalContentRect, containerRect),
        id,
      },
    ];
  });
}

const getContentRectsForItemsWithoutContext = (
  itemsWithoutContext: PostHistoryMagneticWallVersionChanges[],
  quill: Quill,
) => {
  const containerRect = quill.container.getBoundingClientRect();

  // This is to place the items without context and
  // without retain at the top of the magnetic wall
  const relativeZeroRect = toRelativeDOMRect(
    DOMRect.fromRect({
      x: containerRect.x,
      y: containerRect.y,
    }),
    containerRect,
  );

  return _.compact(
    itemsWithoutContext.map((item) => {
      if (item.type === 'comment') {
        const retainIndex = new Delta(
          new Delta(item.thread.context).filter((op) => op.retain != null),
        ).length();

        if (retainIndex === 0) {
          return {
            id: item.id,
            rect: relativeZeroRect,
          };
        }

        const [blot] = quill.getLine(Math.min(retainIndex, quill.getLength()));

        if (blot?.domNode instanceof Element) {
          return {
            id: item.id,
            rect: toRelativeDOMRect(
              blot.domNode.getBoundingClientRect(),
              containerRect,
            ),
          };
        }
      }

      return {
        id: item.id,
        rect: relativeZeroRect,
      };
    }),
  );
};

export const getContentRectsForMagneticWallVersionChanges = (
  versionChanges: PostHistoryMagneticWallVersionChanges[],
  quill: Quill,
) => {
  const contentRects = getMagneticWallItemContentRects(quill);

  const idsWithContext = new Set(contentRects.map((rect) => rect.id));
  const itemsWithoutContext = versionChanges.filter(
    (item) => !idsWithContext.has(item.id),
  );
  const contentRectsForItemsWithoutContext =
    getContentRectsForItemsWithoutContext(itemsWithoutContext, quill);

  return [...contentRectsForItemsWithoutContext, ...contentRects];
};

interface MetaProps {
  children: React.ReactNode;
}

const Meta = ({ children }: MetaProps) => {
  return <div className={styles.versionMeta}>{children}</div>;
};

const BarButton = ({ children, className, ...props }: ButtonProps) => {
  return (
    <Button className={classNames(styles.barButton, className)} {...props}>
      {children}
    </Button>
  );
};

interface TopbarProps {
  children?: React.ReactNode;
  overflowMenu?: React.ReactNode;
}

const Topbar = React.memo(({ children, overflowMenu }: TopbarProps) => {
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);
  const { versionChanges, isInitialized } = useVersionChanges();
  const { focusedIndex, goToNextChange, goToPreviousChange } =
    useVersionChangesFocus();

  const shouldShowCycleBackButton =
    versionChanges.length > 1 && focusedIndex >= versionChanges.length - 1;

  return (
    <div className={styles.bar}>
      <div className={styles.barContent}>
        {children}

        {!!overflowMenu && (
          <OverflowMenu
            placement="bottom-start"
            open={isOverflowMenuOpen}
            onOpenChange={setIsOverflowMenuOpen}
            modifiers={[{ name: 'offset', options: { offset: [0, 2] } }]}
            button={
              <BarButton
                className={classNames(styles.muted, {
                  [styles.active]: isOverflowMenuOpen,
                })}
                aria-label="More options"
              >
                <SVGIcon>{overflowMenuIcon}</SVGIcon>
              </BarButton>
            }
          >
            {overflowMenu}
          </OverflowMenu>
        )}
      </div>
      <div className={styles.cyclingContainer}>
        {isInitialized && (
          <div className={styles.cyclingLabel}>
            {versionChanges.length > 0 ? (
              <>
                {focusedIndex + 1} / {versionChanges.length} changes
              </>
            ) : (
              'No changes'
            )}
          </div>
        )}

        <div className={styles.cyclingButtons}>
          <BarButton
            aria-label="Previous change"
            disabled={focusedIndex <= 0}
            onClick={goToPreviousChange}
          >
            <Caret block size="regular" direction="up" />
          </BarButton>
          {shouldShowCycleBackButton ? (
            <BarButton
              aria-label="Go back to first change"
              onClick={goToNextChange}
            >
              <SVGIcon size="regular">{retryIcon}</SVGIcon>
            </BarButton>
          ) : (
            <BarButton
              aria-label="Next change"
              disabled={focusedIndex >= versionChanges.length - 1}
              onClick={goToNextChange}
            >
              <Caret block size="regular" direction="down" />
            </BarButton>
          )}
        </div>
      </div>
    </div>
  );
});

interface PostLayoutTopbarContainerProps {
  children?: React.ReactNode;
}

const PostLayoutTopbarContainer = ({
  children,
}: PostLayoutTopbarContainerProps) => {
  const isMobile = useIsSize(Sizes.SM);
  const isChromeVisible = usePostChromeVisible({ enabled: isMobile });

  return (
    <PostLayout.StateBar
      className={classNames(styles.barContainer, {
        [styles.hide]: !isChromeVisible,
      })}
    >
      {children}
    </PostLayout.StateBar>
  );
};

const moveFocusedClass = (root: HTMLElement, id: string | null) => {
  const focusedElements = new Set<Element>();
  if (id) {
    root
      .querySelectorAll(`[${VersionChangeFormat.keyName}="${id}"]`)
      .forEach((element) => {
        focusedElements.add(element);
        element.classList.add('focused-diff');
      });
  }

  Array.from(root.getElementsByClassName('focused-diff')).forEach((element) => {
    if (!focusedElements.has(element)) {
      element.classList.remove('focused-diff');
    }
  });
};

const scrollVersionChangeIntoView = (root: HTMLElement, id: string) => {
  const element = root.querySelector<HTMLElement>(
    `[${VersionChangeFormat.keyName}="${id}"]`,
  );
  if (element) {
    scrollIntoView(element, { scrollMode: 'if-needed', behavior: 'smooth' });
  }
};

const useScrollToEditVersionChange = () => {
  const { focusedVersionChange } = useVersionChangesFocus();
  const { quill } = useQuill();
  const prevFocusedId = useRef<string | null>(null);

  useEffect(() => {
    if (!quill || !focusedVersionChange) return;

    const isInitialCall = prevFocusedId.current == null;
    prevFocusedId.current = focusedVersionChange.id;

    if (document.location.hash && isInitialCall) {
      // if we have a hash in the URL, useScrollingToAnchorOnLoad will handle the initial scroll
      return;
    }

    if (focusedVersionChange.type === 'edit') {
      scrollVersionChangeIntoView(quill.root, focusedVersionChange.id);
    }
  }, [quill, focusedVersionChange]);
};
interface PostProps {
  content: Delta | undefined;
}

const Post = React.memo(({ content }: PostProps) => {
  const { quill } = useQuill();
  const { focusedVersionChange } = useVersionChangesFocus();
  const { updateVersionChanges } = useVersionChanges();
  const { focusMagneticWallItem } = useVersionChangeMagneticWallFocus();

  useHeaderCollapse(quill);
  useListCollapse(quill);
  useScrollingToAnchorOnLoad(quill);
  useScrollToEditVersionChange();

  const focusedId = focusedVersionChange?.id ?? null;

  useEffect(() => {
    if (!quill) return;
    moveFocusedClass(quill.root, focusedId);
  }, [quill, focusedId]);

  useEffect(() => {
    if (quill && focusedId) {
      const listCollapseManager = getListCollapse(quill);
      const headerCollapseManager = getHeaderCollapse(quill);

      const element = quill.root.querySelector(
        `[${VersionChangeFormat.keyName}="${focusedId}"], .${Comment.keyName}-${focusedId}, .${Annotation.keyName}-${focusedId}`,
      );
      const blot = quill.scroll.find(element, true);
      if (!blot) return;

      listCollapseManager?.revealBlot(blot);
      headerCollapseManager?.revealBlot(blot);
    }
  }, [quill, focusedId]);

  if (!content) return null;

  return (
    <AttributeHandler
      onAttributeClick={(attribute, type) => {
        focusMagneticWallItem(attribute.id, type);
      }}
    >
      <HistoricalPost
        content={content}
        onBlotComponentsInitialLoad={updateVersionChanges}
      />
    </AttributeHandler>
  );
});

interface VersionChangeState {
  isInitialized: boolean;
  versionChanges: VersionChange[];
  focusedId: string | null;
}

type Action =
  | { type: 'focus'; id: string | null }
  | { type: 'setVersionChanges'; versionChanges: VersionChange[] };

const reducer = (
  state: VersionChangeState,
  action: Action,
): VersionChangeState => {
  switch (action.type) {
    case 'focus':
      return { ...state, focusedId: action.id };
    case 'setVersionChanges': {
      return {
        ...state,
        isInitialized: true,
        versionChanges: action.versionChanges,
        focusedId: action.versionChanges.find((v) => v.id === state.focusedId)
          ? state.focusedId
          : null,
      };
    }
    default:
      return state;
  }
};

const initialState: VersionChangeState = {
  isInitialized: false,
  versionChanges: [],
  focusedId: null,
};

interface RootProps {
  children: React.ReactNode;
  allVersionChanges: VersionChange[];
}

const Root = ({ children, allVersionChanges }: RootProps) => {
  const { quill } = useQuill();

  const [magneticWallFocus, setMagneticWallFocus] =
    useState<MagneticWallFocus | null>(null);
  const [{ isInitialized, versionChanges, focusedId }, reducerDispatch] =
    useReducer(reducer, initialState);
  const currentVersionChangesRef = useRef<typeof allVersionChanges>();

  const focusById = useCallback((id: string | null) => {
    reducerDispatch({ type: 'focus', id });
  }, []);

  const updateVersionChanges = useCallback(
    (quillInstance: Quill) => {
      if (!allVersionChanges) return;

      const magneticWallVersionChanges = allVersionChanges.filter(
        (v) => v.type === 'comment' || v.type === 'annotation',
      );

      const allRects = [
        ...getContentRectsForMagneticWallVersionChanges(
          magneticWallVersionChanges,
          quillInstance,
        ),
        ...getVersionChangeContentRects(quillInstance),
      ];
      const rectsMap = reduceContentRectsToMap(allRects);

      const sortedVersionChanges = allVersionChanges
        .filter((v) => rectsMap[v.id])
        .toSorted((a, b) => {
          return (rectsMap[a.id]?.top ?? 0) - (rectsMap[b.id]?.top ?? 0);
        });

      currentVersionChangesRef.current = sortedVersionChanges;

      reducerDispatch({
        type: 'setVersionChanges',
        versionChanges: sortedVersionChanges,
      });
    },
    [allVersionChanges],
  );

  useEffect(() => {
    if (
      quill &&
      (!currentVersionChangesRef.current ||
        !_.isEqual(allVersionChanges, currentVersionChangesRef.current))
    ) {
      updateVersionChanges(quill);
    }
  }, [
    updateVersionChanges,
    allVersionChanges,
    quill,
    currentVersionChangesRef,
  ]);

  const focusedIndex = useMemo(
    () =>
      focusedId ? versionChanges.findIndex((v) => v.id === focusedId) : -1,
    [versionChanges, focusedId],
  );

  const focusedVersionChange =
    focusedIndex >= 0 ? (versionChanges.at(focusedIndex) ?? null) : null;

  useEffect(() => {
    if (!quill) return undefined;

    const listener = (event: MouseEvent) => {
      if (event.target instanceof Node) {
        const element =
          event.target instanceof Element
            ? event.target
            : event.target.parentElement;
        const id = element
          ?.closest(`[${VersionChangeFormat.keyName}]`)
          ?.getAttribute(VersionChangeFormat.keyName);
        if (id) {
          focusById(id);
        }
      }
    };
    quill.root.addEventListener('click', listener);
    return () => {
      quill.root.removeEventListener('click', listener);
    };
  }, [quill, versionChanges, focusById]);

  const prevFocusedId = useRef<string | null>(null);

  useEffect(() => {
    if (!quill || !focusedId) return;

    const isInitialCall = prevFocusedId.current == null;
    prevFocusedId.current = focusedId;

    if (document.location.hash && isInitialCall) {
      // if we have a hash in the URL, useScrollingToAnchorOnLoad will handle the initial scroll
      return;
    }

    scrollVersionChangeIntoView(quill.root, focusedId);
  }, [quill, focusedId]);

  const magneticWallChangeFocus = useMemo(() => {
    if (magneticWallFocus) return magneticWallFocus;

    if (!focusedVersionChange || focusedVersionChange.type === 'edit') {
      return null;
    }

    return {
      id: focusedVersionChange.id,
      type: focusedVersionChange.type,
      active: true,
      hasExpandIntent: false,
    };
  }, [focusedVersionChange, magneticWallFocus]);

  const focusMagneticWallItem = useCallback(
    (id: string, type: MagneticWallType) => {
      setMagneticWallFocus({
        id,
        type,
        active: true,
        hasExpandIntent: false,
      });

      const versionChange = allVersionChanges?.find(
        (v): v is PostHistoryMagneticWallVersionChanges =>
          v.id === id && v.type === type,
      );

      if (
        (versionChange?.type === 'comment' &&
          versionChange.newCommentIds.size > 0) ||
        (versionChange?.type === 'annotation' && versionChange.isNew)
      ) {
        focusById(versionChange.id);
      }
    },
    [setMagneticWallFocus, focusById, allVersionChanges],
  );

  const versionChangesFocusContextValue =
    useMemo<VersionChangesFocusContextValue>(() => {
      return {
        focusedIndex,
        focusedVersionChange,
        focusById,
        goToNextChange() {
          const newId =
            versionChanges[(focusedIndex + 1) % versionChanges.length]?.id;
          if (!newId) return;
          focusById(newId);
        },
        goToPreviousChange() {
          const newId = versionChanges[focusedIndex - 1]?.id;
          if (!newId) return;
          focusById(newId);
        },
      };
    }, [focusedIndex, focusedVersionChange, focusById, versionChanges]);

  const magneticWallChangeFocusContextValue =
    useMemo<VersionChangeMagneticWallFocusContextValue>(() => {
      return {
        magneticWallChangeFocus,
        focusMagneticWallItem,
        blurMagneticWallFocus: () => setMagneticWallFocus(null),
      };
    }, [magneticWallChangeFocus, focusMagneticWallItem]);

  const versionChangesContextValue = useMemo<VersionChangesContextValue>(() => {
    return {
      isInitialized,
      versionChanges,
      updateVersionChanges,
    };
  }, [isInitialized, versionChanges, updateVersionChanges]);

  return (
    <VersionChangesContext.Provider value={versionChangesContextValue}>
      <VersionChangesFocusContext.Provider
        value={versionChangesFocusContextValue}
      >
        <VersionChangeMagneticWallFocusContext.Provider
          value={magneticWallChangeFocusContextValue}
        >
          {children}
        </VersionChangeMagneticWallFocusContext.Provider>
      </VersionChangesFocusContext.Provider>
    </VersionChangesContext.Provider>
  );
};

export default {
  Root,
  Topbar,
  PostLayoutTopbarContainer,
  Post,
  Meta,
  BarButton,
};
